import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const InstallContext = createContext({
  isStandalone: false,
  canInstall: false,
  promptInstall: async () => ({ status: "unavailable" }),
});

export function InstallProvider({ children }) {
  const [isStandalone, setIsStandalone] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    const updateStandaloneState = () => {
      const standalone =
        (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        (typeof window !== "undefined" &&
          window.navigator &&
          // iOS Safari
          "standalone" in window.navigator &&
          window.navigator.standalone);
      setIsStandalone(Boolean(standalone));
    };
    updateStandaloneState();

    const handleBeforeInstallPrompt = (e) => {
      // Chrome/Edge: intercept to trigger on demand
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setIsStandalone(true);
      deferredPromptRef.current = null;
      setCanInstall(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    document.addEventListener("visibilitychange", updateStandaloneState);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      document.removeEventListener("visibilitychange", updateStandaloneState);
    };
  }, []);

  const promptInstall = async () => {
    const promptEvent = deferredPromptRef.current;
    if (!promptEvent) return { status: "unavailable" };
    try {
      promptEvent.prompt();
      const { outcome } = await promptEvent.userChoice;
      // The event can only be used once
      deferredPromptRef.current = null;
      setCanInstall(false);
      if (outcome === "accepted") {
        return { status: "accepted" };
      }
      return { status: "dismissed" };
    } catch {
      return { status: "error" };
    }
  };

  return (
    <InstallContext.Provider value={{ isStandalone, canInstall, promptInstall }}>
      {children}
    </InstallContext.Provider>
  );
}

export function useInstall() {
  return useContext(InstallContext);
}

